<template>
  <UIModal
    :is-open="show"
    @close="$emit('close')"
    :title="modalTitle"
    size="md"
  >
    <div class="p-4">
      <p class="mb-4 text-gray-700 dark:text-gray-300">
        {{ messageText }}
      </p>

      <div v-if="isSubscribeContext" class="mb-4 flex justify-center">
        <router-link
          to="/register"
          class="inline-flex items-center justify-center px-4 py-2 rounded-lg font-medium bg-primary text-white hover:bg-primary-accent focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          @click="$emit('close')"
        >
          {{ $t('auth.loginModal.createAccount') }}
        </router-link>
      </div>

      <div v-if="isSubscribeContext" class="my-4 border-t border-gray-200 dark:border-gray-600" role="separator" />

      <LoginForm @success="handleLoginSuccess" @error="handleLoginError" :is-popup="true" />

      <div class="mt-4 text-center space-y-4">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          {{ $t('auth.login.noAccount') }}
          <router-link
            to="/register"
            class="font-bold text-primary dark:text-white hover:text-primary-accent"
            @click="$emit('close')"
          >
            {{ $t('auth.login.signUp') }}
          </router-link>
        </p>
        <p class="mt-6 text-sm text-gray-600 dark:text-gray-400">
          <UIButton
            @click="handleContinueAsGuest"
            variant="plain"
            size="sm"
          >
            {{ $t('auth.login.continueAsGuest') }}
          </UIButton>
        </p>
      </div>

      <div class="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
        {{ $t('auth.login.agreeToTerms') }}
        <router-link
          to="/terms"
          class="font-bold text-primary hover:text-primary-accent dark:text-white dark:hover:text-gray-200"
          @click="$emit('close')"
        >
          {{ $t('auth.login.termsOfService') }}
        </router-link>
        {{ $t('common.labels.and') }}
        <router-link
          to="/privacy-policy"
          class="font-bold text-primary hover:text-primary-accent dark:text-white dark:hover:text-gray-200"
          @click="$emit('close')"
        >
          {{ $t('auth.login.privacyPolicy') }}
        </router-link>
      </div>
    </div>
  </UIModal>
</template>

<script setup lang="ts">
import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('LoginModal')

import { defineProps, defineEmits, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import UIModal from '@/components/ui/UIModal.vue'
import LoginForm from '@/components/forms/LoginForm.vue'
import { useLoginPopup } from '@/composables/useLoginPopup'
import UIButton from '@/components/ui/UIButton.vue'
import type { LoginPopupReason } from '@/composables/useLoginPopup'

const props = defineProps<{
  show: boolean
  context?: LoginPopupReason
}>()

const { t } = useI18n()
const { loginPopupContext: injectedContext, cancelRedirect } = useLoginPopup()

// Read context from the same ref that showLoginPopup() sets (reliable when modal opens)
const effectiveContext = computed<LoginPopupReason>(() => {
  const ctx = injectedContext?.value ?? props.context
  return ctx === 'subscribe' ? 'subscribe' : 'default'
})

const isSubscribeContext = computed(() => effectiveContext.value === 'subscribe')

const messageText = computed(() =>
  isSubscribeContext.value
    ? t('auth.loginModal.subscribeOrPurchaseRequired')
    : t('auth.loginModal.signInRequired')
)

const modalTitle = computed(() => t('auth.loginModal.title'))

const emit = defineEmits(['close', 'success', 'continueAsGuest', 'error'])

const handleLoginSuccess = () => {
  emit('success')
  emit('close')
}

const handleLoginError = (error: Error | unknown) => {
  fileLogger.log('Login error:', error)
  
  // Emit the error but keep the modal open
  emit('error', error)
  
  // Use the cancelRedirect function to prevent unwanted redirects
  cancelRedirect()
  
  // Ensure we don't navigate away from the current page
  // Add a small delay to make sure any potential redirect is canceled
  setTimeout(() => {
    const currentPath = window.location.pathname
    const loginPath = '/login'
    
    // If somehow we ended up on the login page, go back to previous page
    if (currentPath === loginPath) {
      fileLogger.log('Detected redirect to login, going back')
      window.history.back()
    }
  }, 100)
}

const handleContinueAsGuest = () => {
  emit('continueAsGuest')
  emit('close')
  
  // Cancel any pending redirects when user continues as guest
  cancelRedirect()
}
</script> 